import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import styles from './Users.module.css';

interface UserFormData {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  organization_id: string | number | null;
  is_active: boolean;
  role_ids: number[];
}

export const Users: React.FC = () => {
  const { user, token } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState<string>('all');
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    organization_id: '',
    is_active: true,
    role_ids: [],
  });

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, orgsRes, rolesRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL}/api/users`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${import.meta.env.VITE_API_URL}/api/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${import.meta.env.VITE_API_URL}/api/users/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const usersData = await usersRes.json();
      const orgsData = await orgsRes.json();
      const rolesData = await rolesRes.json();

      setUsers(usersData.users || []);
      setOrganizations(orgsData.organizations || []);
      setRoles(rolesData.roles || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Filter users when selection changes
    if (selectedOrgFilter === 'all') {
      setFilteredUsers(users);
    } else if (selectedOrgFilter === 'none') {
      setFilteredUsers(users.filter((u) => !u.organization_id));
    } else {
      const orgId = parseInt(selectedOrgFilter, 10);
      setFilteredUsers(users.filter((u) => u.organization_id === orgId));
    }
  }, [selectedOrgFilter, users]);

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      organization_id: user.permissions?.includes('*') ? '' : user.organizationId,
      is_active: true,
      role_ids: [],
    });
    setShowModal(true);
  };

  const handleEdit = (editUser: any) => {
    setEditingUser(editUser);
    setFormData({
      username: editUser.username,
      email: editUser.email,
      password: '',
      first_name: editUser.first_name || '',
      last_name: editUser.last_name || '',
      organization_id: editUser.organization_id || '',
      is_active: editUser.is_active === 1,
      role_ids: editUser.role_ids || [],
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const submitData = { ...formData };

    // Remove password field if editing and password is empty
    if (editingUser && (!submitData.password || submitData.password.trim() === '')) {
      delete submitData.password;
    }

    try {
      const url = editingUser
        ? `${import.meta.env.VITE_API_URL}/api/users/${editingUser.id}`
        : `${import.meta.env.VITE_API_URL}/api/users`;
      const response = await fetch(url, {
        method: editingUser ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        toast.success(editingUser ? 'User updated' : 'User created');
        setShowModal(false);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.message || data.error || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Failed to save user');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/users/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('User deleted');
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleRoleToggle = (roleId: number) => {
    setFormData((prev) => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((id) => id !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  const getOrganizationName = (orgId: number | null): string => {
    const org = organizations.find((o) => o.id === orgId);
    return org ? org.name : 'System';
  };

  const getRoleNames = (roleIds: number[] | null): string => {
    if (!roleIds || roleIds.length === 0) return 'No roles';
    return roleIds
      .map((id) => {
        const role = roles.find((r) => r.id === id);
        return role ? role.name : '';
      })
      .filter(Boolean)
      .join(', ');
  };

  if (loading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.title}>Users</h2>
          {user.permissions?.includes('*') && organizations.length > 0 && (
            <select
              className={styles.filterSelect}
              value={selectedOrgFilter}
              onChange={(e) => setSelectedOrgFilter(e.target.value)}
            >
              <option value="all">All Organizations</option>
              <option value="none">No Organization (System)</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <button className={styles.button} onClick={handleCreate}>
          + Create User
        </button>
      </div>

      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th className={styles.th}>Username</th>
            <th className={styles.th}>Email</th>
            <th className={styles.th}>Name</th>
            <th className={styles.th}>Organization</th>
            <th className={styles.th}>Roles</th>
            <th className={styles.th}>Status</th>
            <th className={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((u) => (
            <tr className={styles.tr} key={u.id}>
              <td className={styles.td}>{u.username}</td>
              <td className={styles.td}>{u.email}</td>
              <td className={styles.td}>{u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : '-'}</td>
              <td className={styles.td}>{getOrganizationName(u.organization_id)}</td>
              <td className={styles.td}>{getRoleNames(u.role_ids)}</td>
              <td className={styles.td}>
                <span className={styles.statusBadge} data-active={String(u.is_active === 1)}>
                  {u.is_active === 1 ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className={styles.td}>
                <button className={styles.actionButton} onClick={() => handleEdit(u)}>
                  Edit
                </button>
                <button className={styles.actionButton} onClick={() => handleDelete(u.id)}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filteredUsers.length === 0 && users.length > 0 && (
        <div className={styles.emptyState}>No users found for the selected organization.</div>
      )}

      {users.length === 0 && <div className={styles.emptyState}>No users found. Create one to get started.</div>}

      {showModal && (
        <div className={styles.modal} onClick={() => setShowModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>{editingUser ? 'Edit User' : 'Create User'}</h3>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Username *</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  disabled={!!editingUser}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Email *</label>
                <input
                  className={styles.input}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Password {editingUser ? '(leave blank to keep current)' : '*'}</label>
                <input
                  className={styles.input}
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  minLength={8}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>First Name</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Last Name</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
              </div>

              {user.permissions?.includes('*') && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>Organization</label>
                  <select
                    className={styles.select}
                    value={formData.organization_id ?? ''}
                    onChange={(e) => setFormData({ ...formData, organization_id: e.target.value || null })}
                  >
                    <option value="">System / No Organization</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className={styles.formGroup}>
                <label className={styles.label}>Roles *</label>
                <div className={styles.checkboxGroup}>
                  {roles.map((role) => (
                    <label className={styles.checkboxLabel} key={role.id}>
                      <input
                        className={styles.checkbox}
                        type="checkbox"
                        checked={formData.role_ids.includes(role.id)}
                        onChange={() => handleRoleToggle(role.id)}
                      />
                      {role.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.button}
                  type="button"
                  data-variant="secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button className={styles.button} type="submit">
                  {editingUser ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
